import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import Conversation from "./Conversation";
import ChatInput from "./ChatInput";
import Icon from "../../YourAIAgents/components/components/icon";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import TaskCard from "./WelcomeSections/TaskCard";
import TasksFilterBar, { NO_TOOL_VALUE } from "./WelcomeSections/TasksFilterBar";
import useTaskCardActions from "../hooks/useTaskCardActions";
import suggestAutomation from "../utils/suggestAutomation";
import { formatCadence, isScheduled } from "../utils/taskSchedule";
import { PROVEN_TASK_TYPES } from "../utils/provenTasks";
import { useAgencyPromptData, useTeamPromptData } from "../../../queries/tasks";
import { useAvailableIntegrations } from "../../../queries/integrations";

const formatOutputLabel = (value) => {
  if (!value) return "Unknown";
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};


const ConversationWrapper = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const { mutateAsync: fetchAgencyPromptData } = useAgencyPromptData();

  // The Task Library shows a compact preview by default; "Your Tasks" (nav) and
  // the "Explore more" card expand it to the full grid via the #task-library
  // hash. This is what replaced the standalone All-Tasks page.
  const libraryExpanded = location.hash === "#task-library";
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [scope, setScope] = useState("you");
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]); // teammate ids, excludes self
  const [prefillMessage, setPrefillMessage] = useState("");
  const [provenTasks, setProvenTasks] = useState([]);
  const [yourTasksLoaded, setYourTasksLoaded] = useState(false);
  const [provenTasksLoaded, setProvenTasksLoaded] = useState(false);
  const [teamTasksData, setTeamTasksData] = useState(null);
  const [teamTasksLoaded, setTeamTasksLoaded] = useState(false);
  const teamTasksLoadingRef = useRef(false);

  const {
    selectedAssistant,
    handleSendMessage,
    setMessages,
    createConvesation,
    tasksData,
    fetchTasksData,
    resetAttachedFileSize,
    setIsUserChating,
    isUserChating,
    markMessagePending,
    contextuploadFilefrominput,
    locationUsers,
    requestLocationUsers,
    agency,
  } = useUsersContext();
  const { mutateAsync: fetchTeamPromptData } = useTeamPromptData();
  const { data: availableIntegrationsData } = useAvailableIntegrations(
    userActiveLocation?.id,
    userInfo?.userId,
    userActiveLocation?.agency_id,
  );

  useEffect(() => {
    if (selectedFiles.length === 0) resetAttachedFileSize?.();
  }, [selectedFiles]);

  const { bindActions, actionModals } = useTaskCardActions();

  useEffect(() => {
    fetchTasksData?.()?.finally(() => setYourTasksLoaded(true));
  }, []);

  // The Team tab needs the roster to list teammates to toggle — fetched
  // eagerly (same lazy-but-unconditional pattern as ShareModal) rather than
  // gated behind scope==="team", since reaching "team" scope in the first
  // place usually means picking a teammate from this exact list.
  useEffect(() => {
    requestLocationUsers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const agencyId = agency?._id;
    if (!agencyId) return;
    fetchAgencyPromptData({ agencyId })
      .then((response) => {
        setProvenTasks(response?.data?.prompt || []);
      })
      .catch((err) => {
        console.error("[ProvenTasks] request failed", err);
      })
      .finally(() => setProvenTasksLoaded(true));
  }, [agency, fetchAgencyPromptData]);


  useEffect(() => {
    if (scope !== "team" && scope !== "scheduled") return;
    if (teamTasksLoaded || teamTasksLoadingRef.current) return;
    teamTasksLoadingRef.current = true;
    fetchTeamPromptData({
      userId: userInfo?._id,
      locationId: userActiveLocation?.id,
      agencyId: userActiveLocation?.agency_id,
    })
      .then((response) => {
        setTeamTasksData(response?.data?.prompt || []);
      })
      .catch((err) => {
        console.error("[TeamTasks] request failed", err);
      })
      .finally(() => {
        teamTasksLoadingRef.current = false;
        setTeamTasksLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, teamTasksLoaded]);


  useEffect(() => {
    if (!libraryExpanded) return;
    const id = setTimeout(() => {
      document
        .getElementById("task-library")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(id);
  }, [libraryExpanded]);

  const handlePrefillConsumed = useCallback(() => setPrefillMessage(""), []);

  const toggleTool = useCallback((tool) => {
    setSelectedTools((prev) =>
      prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]
    );
  }, []);

  const clearTools = useCallback(() => setSelectedTools([]), []);


  const normaliseToolKey = (value) =>
    String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Slack/MS Teams tasks record their destination on `output` rather than
  // `integrations`, so they're folded into the same tool-name list here.
  const CHANNEL_OUTPUT_KEYS = new Set(["slack", "microsoftteams"]);
  const getTaskToolNames = (t) => {
    const integrations = Array.isArray(t?.integrations) ? t.integrations : [];
    return CHANNEL_OUTPUT_KEYS.has(normaliseToolKey(t?.output))
      ? [...integrations, t.output]
      : integrations;
  };
  const connectedToolKeys = useMemo(() => {
    const list = availableIntegrationsData?.connected || [];
    return new Set(
      list
        .map((item) => normaliseToolKey(item?.provider_name || item?.provider || item?.name))
        .filter(Boolean),
    );
  }, [availableIntegrationsData]);

  const excludeCurated = (list) =>
    (list || []).filter((t) => {
      const type = String(t?.type);
      return type !== "SuperAdmin" && type !== "super-admin";
    });

  const yourPool = useMemo(() => excludeCurated(tasksData?.prompt), [tasksData]);
  const wholeTeamPool = useMemo(() => excludeCurated(teamTasksData), [teamTasksData]);
  const scheduledPool = useMemo(() => wholeTeamPool.filter(isScheduled), [wholeTeamPool]);
  const provenPool = useMemo(
    () => provenTasks.filter((t) => PROVEN_TASK_TYPES.includes(String(t?.type))),
    [provenTasks],
  );
  // Narrows "Whole team" to specific teammates picked in the Team tab; empty
  // selection means the unfiltered whole-team pool.
  const teamFilteredPool = useMemo(() => {
    if (selectedTeamMembers.length === 0) return wholeTeamPool;
    return wholeTeamPool.filter((t) => {
      const owners = Array.isArray(t?.userId) ? t.userId : [];
      return owners.some((owner) => selectedTeamMembers.includes(String(owner?.id)));
    });
  }, [wholeTeamPool, selectedTeamMembers]);

  // Tool counts/no-tool count are scoped to the active Show scope (before
  // tool filtering is applied), same data the task grid itself draws from.
  const scopeBaseList = useMemo(() => {
    if (scope === "team") return teamFilteredPool;
    if (scope === "scheduled") return scheduledPool;
    if (scope === "proven") return provenPool;
    return yourPool;
  }, [scope, teamFilteredPool, scheduledPool, provenPool, yourPool]);

  const { toolMatchCounts, noToolCount } = useMemo(() => {
    const counts = new Map();
    let none = 0;
    scopeBaseList.forEach((t) => {
      const names = getTaskToolNames(t);
      if (names.length === 0) {
        none += 1;
        return;
      }
      names.forEach((name) => {
        const key = normaliseToolKey(name);
        if (key) counts.set(key, (counts.get(key) || 0) + 1);
      });
    });
    return { toolMatchCounts: counts, noToolCount: none };
  }, [scopeBaseList]);

  // The Tools tab should list every tool this location can connect, not only
  // the ones already attached to a task — so it's seeded from both the
  // actual task integrations AND the full /integrations/available catalog,
  // deduped by a loosely-normalised key since the two sources spell providers
  // differently ("Google Docs" task integration vs "google-docs" Nango
  // provider key).
  const toolOptions = useMemo(() => {
    const seen = new Map();
    const addEntry = (rawValue) => {
      const key = normaliseToolKey(rawValue);
      if (!key || key === "chatui" || seen.has(key)) return;
      seen.set(key, { value: rawValue, label: formatOutputLabel(rawValue) });
    };
    (tasksData?.prompt || []).forEach((item) =>
      getTaskToolNames(item).forEach(addEntry),
    );
    (availableIntegrationsData?.integrations || []).forEach((item) =>
      addEntry(item?.provider_name || item?.provider || item?.name),
    );
    return Array.from(seen.entries())
      .map(([key, opt]) => ({
        ...opt,
        connected: connectedToolKeys.has(key),
        count: toolMatchCounts.get(key) || 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasksData, availableIntegrationsData, connectedToolKeys, toolMatchCounts]);

  const scopedList = scopeBaseList;

  const filteredTasks = useMemo(() => {
    const list = scopedList;
    const term = search.trim().toLowerCase();
    const wantsNoTool = selectedTools.includes(NO_TOOL_VALUE);
    // Normalised the same way as toolMatchCounts/connectedToolKeys, since a
    // selected tool's value can come from either a task's integrations entry
    // or an integration's provider key, which spell the same tool
    // differently ("Google Docs" vs "google-docs").
    const toolSelections = selectedTools
      .filter((s) => s !== NO_TOOL_VALUE)
      .map((s) => normaliseToolKey(s));
    return list.filter((t) => {
      if (selectedTools.length > 0) {
        const normalisedNames = getTaskToolNames(t).map((name) => normaliseToolKey(name));
        const matchesNoTool = wantsNoTool && normalisedNames.length === 0;
        const matchesTool =
          toolSelections.length > 0 &&
          toolSelections.some((sel) => normalisedNames.includes(sel));
        if (!matchesNoTool && !matchesTool) return false;
      }
      if (!term) return true;
      return (
        (t?.title || "").toLowerCase().includes(term) ||
        (t?.prompt_value || "").toLowerCase().includes(term)
      );
    });
  }, [scopedList, search, selectedTools]);

  const isLibraryLoading =
    scope === "proven"
      ? !provenTasksLoaded
      : scope === "team" || scope === "scheduled"
        ? !teamTasksLoaded
        : !yourTasksLoaded;

  // Real names for the Team tab + the active-scope chip. locationUsers is the
  // same source ShareModal uses for "share with a teammate".
  const teamRoster = useMemo(() => {
    const meId = String(userInfo?._id || "");
    const mapped = (locationUsers || []).map((u) => ({
      id: String(u?._id),
      name: u?.userName || "Teammate",
      isCurrentUser: String(u?._id) === meId,
    }));
    if (meId && !mapped.some((m) => m.isCurrentUser)) {
      mapped.unshift({ id: meId, name: userInfo?.userName || "You", isCurrentUser: true });
    }
    return mapped.sort((a, b) => (a.isCurrentUser === b.isCurrentUser ? 0 : a.isCurrentUser ? -1 : 1));
  }, [locationUsers, userInfo]);

  const toggleTeamMember = useCallback((memberId, isCurrentUser) => {
    if (isCurrentUser) {
      setScope("you");
      setSelectedTeamMembers([]);
      return;
    }
    setScope("team");
    setSelectedTeamMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  }, []);

  const handleScopeChange = useCallback((nextScope) => {
    setScope(nextScope);
    if (nextScope !== "team") setSelectedTeamMembers([]);
  }, []);

  const resetScope = useCallback(() => {
    setScope("you");
    setSelectedTeamMembers([]);
  }, []);

  const clearAllFilters = useCallback(() => {
    resetScope();
    clearTools();
  }, [resetScope, clearTools]);

  const scopeChipLabel = useMemo(() => {
    if (scope === "scheduled") return "Scheduled";
    if (scope === "proven") return "Proven";
    if (scope === "team") {
      if (selectedTeamMembers.length === 0) return "Whole team";
      if (selectedTeamMembers.length === 1) {
        return teamRoster.find((m) => m.id === selectedTeamMembers[0])?.name || "Whole team";
      }
      return `${selectedTeamMembers.length} teammates`;
    }
    return null;
  }, [scope, selectedTeamMembers, teamRoster]);

  const handleTaskCardClick = useCallback((task) => {
    const taskPrompt = task?.prompt_value?.trim();
    if (!taskPrompt) {
      toast.error("This task does not have prompt content to run.");
      return;
    }
    setPrefillMessage(taskPrompt);
  }, []);

  const submitNewMessage = async (
    prompt,
    type = "savetodb",
    messagetype = "notimage",
    setNewMessage,
    addInMessages = true,
  ) => {
    setIsUserChating(true)
    if (selectedFiles.length > 10) {
      const container = document.getElementById("errorofmaxfile");
      if (container) {
        const div = document.createElement("div");
        div.style.display = "flex";
        div.style.justifyContent = "center";
        div.style.position = "relative";
        div.className = "file_upload_input";
        div.innerHTML = `
        <span style="position: absolute; top: 5px; right: 10px; cursor: pointer;" onclick="this.parentElement.remove()">&#10005;</span>
        <p style="padding: 15px; margin: 0px; color: red; text-align: center;">
            You can only upload 10 files at a time
        </p>
      `;
        container.appendChild(div);
        setTimeout(() => {
          div.remove();
        }, 5000);
      }
      return true;
    }

    let resolvedFiles = selectedFiles;
    const pendingFiles = selectedFiles.filter((f) => f instanceof File);
    if (pendingFiles.length > 0) {
      try {
        setIsUserChating(true)
        const response = await contextuploadFilefrominput(
          pendingFiles,
          params?.id,
          () => { },
        );
        console.log("upload response:", response?.data);
        if (response?.data?.success === true && response?.data?.data) {
          const uploaded = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
          let idx = 0;
          resolvedFiles = selectedFiles
            .map((f) => (f instanceof File ? uploaded[idx++] : f))
            .filter(Boolean);
        } else {
          resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
        }
      } catch (err) {
        console.error("file upload failed:", err);
        resolvedFiles = selectedFiles.filter((f) => !(f instanceof File));
      }
    }

    const fileToUpload = JSON.parse(JSON.stringify(resolvedFiles));
    const effectivePrompt =
      prompt && prompt.trim()
        ? prompt
        : fileToUpload?.length > 0
          ? ""
          : prompt;
    setSelectedFiles([]);
    if (!params.id) {
      const payload = {
        fileData: fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })),
        message: effectivePrompt,
        messagetype,
        addInMessages,
      };
      createConvesation(payload);
      setNewMessage && setNewMessage("");
      return;
    }

    const newMsgObject = {
      content: effectivePrompt,
      sender: null,
      time: new Date().toLocaleTimeString(),
      status: "delivered",
      file_type: messagetype,
      assistant_name: selectedAssistant?.assistant_name || "",
      file_ids:
        fileToUpload?.map((file) => ({
          id: file.id,
          name: file.name,
        })) || [],
    };

    const messagePayload = {
      conversation_id: params?.id,
      type: type,
      message: newMsgObject,
    };
    handleSendMessage(messagePayload);
    setNewMessage("");
    if (addInMessages) {
      setMessages((prev) => [...prev, newMsgObject]);
      markMessagePending(newMsgObject);
    }
    document.getElementById("gotovie")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      // In a conversation the bottom padding moves off the scroll panel so the
      // sticky composer sits flush with the panel edge (no see-through strip).
      className={`h-[calc(100vh-85px)] overflow-y-auto max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 ${params.id ? "pt-8" : "pt-14 md:pt-20 pb-16"} [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]`}
    >
      {!params.id ? (
        <div className="max-w-3xl mx-auto w-full mb-8 text-left">
          <div className="page-eyebrow mb-3">Tasks</div>
          <h1 className="page-display text-3xl md:text-4xl">
            <span className="capitalize">Hi {userInfo?.userName}.</span>{" "}
            <span className="accent">Start with one Task.</span>
          </h1>

          {isUserChating && (
            <div className="relative mt-5">
              {selectedFiles?.length > 0 ? (
                <div className="flex items-center gap-1 text-muted-foreground text-[14px]">
                  <Icon
                    id="aichatIconInput"
                    style={{ marginRight: "5px", fontSize: "12px" }}
                  />
                  Analysing files
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground text-[14px]">
                  <CircularProgress size={16} />
                  Starting your task...
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Conversation
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
        />
      )}

      <div
        // In a conversation the composer pins to the bottom of the scroll
        // panel so it never drops off-screen as messages grow (CU-86carbt97).
        className={`max-w-3xl mx-auto w-full ${params.id ? "sticky bottom-0 z-10 bg-background" : ""
          }`}
      >
        {!params.id && <div id="errorofmaxfile" />}
        <ChatInput
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          submitNewMessage={submitNewMessage}
          prefillMessage={prefillMessage}
          onPrefillConsumed={handlePrefillConsumed}
        />

        <div className="flex items-center justify-end mt-5 pb-6 px-2">
          <span className="inline-flex items-center gap-2 text-[11px] text-muted-foreground">
            <span aria-hidden className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-[hsl(var(--task-accent))] opacity-60 animate-ping" />
              <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--task-accent))]" />
            </span>
            Best AI picked for every Task
          </span>
        </div>
      </div>

      {selectedAssistant?.assistant_name !== 'Agent Builder' && <div id="task-library" className="mt-8 scroll-mt-6">
        <TasksFilterBar
          scope={scope}
          onScopeChange={handleScopeChange}
          scopeChipLabel={scopeChipLabel}
          onResetScope={resetScope}
          counts={{
            you: yourPool.length,
            team: wholeTeamPool.length,
            scheduled: scheduledPool.length,
            proven: provenPool.length,
          }}
          count={filteredTasks.length}
          selectedTools={selectedTools}
          onToggleTool={toggleTool}
          toolOptions={toolOptions}
          noToolCount={noToolCount}
          teamMembers={teamRoster}
          selectedTeamMembers={selectedTeamMembers}
          onToggleTeamMember={toggleTeamMember}
          onClearAll={clearAllFilters}
        />

        {isLibraryLoading && filteredTasks.length === 0 ? (
          // Skeleton grid on FIRST load only, per tab — same shape as
          // YourAIAgents.jsx's agent skeleton, sized to TaskCard instead.
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="rounded-xl p-5 bg-card border border-border h-full flex flex-col animate-pulse"
              >
                <div className="h-5 w-3/4 rounded bg-muted mb-2" />
                <div className="h-3.5 w-full rounded bg-muted mb-1.5" />
                <div className="h-3.5 w-5/6 rounded bg-muted mb-5" />
                <div className="mt-auto flex flex-col gap-3">
                  <div className="h-3.5 w-24 rounded bg-muted" />
                  <div className="h-9 w-full rounded-md bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(libraryExpanded ? filteredTasks : filteredTasks.slice(0, 5)).map((task) => {
              const isChatUi =
                !task.output || task.output.toLowerCase() === "chatui";
              const tools = isChatUi ? [] : [task.output];
              return (
                <TaskCard
                  key={task._id || task.id}
                  title={task.title}
                  description={task.prompt_value}
                  metaLabel={
                    task.repeat && task.repeat !== "none"
                      ? `Repeats ${task.repeat}`
                      : "Run anytime"
                  }
                  tools={tools}
                  buttonLabel="Run now"
                  scheduleLabel={
                    isScheduled(task) ? formatCadence(task.repeat) : undefined
                  }
                  suggestedLabel={
                    isScheduled(task)
                      ? undefined
                      : suggestAutomation({
                        title: task.title,
                        description: task.prompt_value,
                      })
                  }
                  onClick={() => handleTaskCardClick(task)}
                  {...bindActions(task)}
                />
              );
            })}

            {!libraryExpanded && filteredTasks.length - 5 > 0 && (
              <TaskCard
                title={`Explore ${filteredTasks.length - 5}+ tasks`}
                description="Sales, ops, HR, finance and more — or write your own from scratch."
                metaLabel="Ready-made templates"
                buttonLabel="Browse tasks"
                showActions={false}
                onClick={() => navigate("#task-library")}
              />
            )}
          </div>
        )}

        {scope !== "proven" && (
          <button
            type="button"
            onClick={() => {
              setScope("proven");
              setSelectedTeamMembers([]);
              setSearch("");
              clearTools();
            }}
            className="interactive-surface mt-4 w-full rounded-xl border border-dashed border-border/70 bg-transparent px-5 py-4 flex items-center justify-between text-left group"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                Browse Proven Tasks Templates
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                The collective playbook. Battle-tested by teams across a range
                of sectors, ready for you to run in seconds.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground group-hover:text-foreground transition-colors shrink-0 ml-4">
              Proven Tasks
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </button>
        )}

        {libraryExpanded && filteredTasks.length > 5 && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() =>
                navigate(
                  { pathname: location.pathname, search: location.search, hash: "" },
                  { replace: true }
                )
              }
              className="text-[13px] text-muted-foreground underline hover:text-foreground"
            >
              Show fewer
            </button>
          </div>
        )}

        {!isLibraryLoading && filteredTasks.length === 0 && (
          <div className="text-center text-[13px] text-muted-foreground py-12">
            No tasks match your filters.
          </div>
        )}
      </div>}

      {!params.id && (
        <footer className="mt-12 border-t border-border/40 py-4">
          <p className="text-xs text-muted-foreground/60 text-center">
            Your data isn't used to train models. AI can make mistakes, so
            review before you ship.
          </p>
        </footer>
      )}

      {actionModals}
    </div>
  );
};

export default ConversationWrapper;
